/**
 * Producers index: sidebar filters, sort, market-image swap, and URL sync.
 * Deep links: ?market=&region=&type=&q=
 */
(function () {
  const PARAM_KEYS = {
    market: 'market',
    region: 'region',
    type: 'type',
    q: 'q',
  };

  function isLg() {
    return window.matchMedia('(min-width: 1024px)').matches;
  }

  function selectedValues(root, group) {
    return Array.from(root.querySelectorAll(`[data-filter-checkbox="${group}"]:checked`)).map(
      (input) => input.value
    );
  }

  function parseListParam(value) {
    if (!value) return [];
    return value
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
  }

  function readUrlState() {
    const params = new URLSearchParams(window.location.search);
    return {
      markets: parseListParam(params.get(PARAM_KEYS.market)),
      regions: parseListParam(params.get(PARAM_KEYS.region)),
      types: parseListParam(params.get(PARAM_KEYS.type)),
      q: params.get(PARAM_KEYS.q) || '',
    };
  }

  function writeUrlState({ markets, regions, types, q }) {
    const url = new URL(window.location.href);
    const setOrDelete = (key, values) => {
      if (values && values.length) {
        url.searchParams.set(key, values.join(','));
      } else {
        url.searchParams.delete(key);
      }
    };
    setOrDelete(PARAM_KEYS.market, markets);
    setOrDelete(PARAM_KEYS.region, regions);
    setOrDelete(PARAM_KEYS.type, types);
    if (q && q.trim()) {
      url.searchParams.set(PARAM_KEYS.q, q.trim());
    } else {
      url.searchParams.delete(PARAM_KEYS.q);
    }
    const next = url.pathname + url.search + url.hash;
    const current = window.location.pathname + window.location.search + window.location.hash;
    if (next !== current) {
      window.history.replaceState({}, '', next);
    }
  }

  function applyCheckboxGroup(root, group, values) {
    const wanted = new Set(values || []);
    root.querySelectorAll(`[data-filter-checkbox="${group}"]`).forEach((input) => {
      input.checked = wanted.has(input.value);
    });
  }

  function itemMatchesMarkets(item, markets) {
    if (!markets.length) return true;
    const itemMarkets = (item.dataset.markets || '')
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean);
    if (!itemMarkets.length) return false;
    return markets.some((market) => itemMarkets.includes(market));
  }

  /**
   * When exactly one market filter is active, prefer that market's override image.
   * Otherwise (none / multiple) use the default collection image.
   */
  function syncMarketImages(items, markets) {
    const marketKey = markets.length === 1 ? markets[0] : '';

    items.forEach((item) => {
      const img = item.querySelector('.card__media img');
      if (!img) return;

      const override = marketKey
        ? item.getAttribute(`data-image-${marketKey}`)
        : null;
      const url = override || item.getAttribute('data-image-default');
      if (!url) return;
      if (img.dataset.producerSrc === url) return;

      img.dataset.producerSrc = url;
      img.src = url;
      img.srcset = `${url} 800w`;
    });
  }

  function init(root) {
    if (!root || root.dataset.producersIndexReady) return;
    root.dataset.producersIndexReady = 'true';

    const items = Array.from(root.querySelectorAll('[data-producer-item]'));
    const empty = root.querySelector('[data-producer-empty]');
    const search = root.querySelector('[data-producer-search]');
    const grid = root.querySelector('[data-producer-grid]');
    const filtersColumn = root.querySelector('[data-producer-filters-column]');
    const facets = root.querySelector('[data-producer-facets]');
    const filterToggle = root.querySelector('[data-producer-filter-toggle]');
    const state = { q: '', sort: 'title-ascending', filtersOpen: true, syncingUrl: false };

    function syncFacetsChrome() {
      if (!facets) return;
      if (isLg()) {
        facets.setAttribute('open', '');
        facets.setAttribute('aria-hidden', 'false');
        facets.removeAttribute('aria-modal');
        facets.removeAttribute('role');
        facets.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
      } else {
        facets.removeAttribute('open');
        facets.setAttribute('role', 'dialog');
        facets.setAttribute('aria-modal', 'true');
        facets.setAttribute('aria-hidden', facets.classList.contains('is-open') ? 'false' : 'true');
      }
    }

    const pageOverlay = document.querySelector('.js-overlay');

    function setPageOverlay(show) {
      if (!pageOverlay) return;
      pageOverlay.classList.toggle('is-visible', show);
    }

    function setFiltersOpen(open) {
      state.filtersOpen = open;
      if (filterToggle) {
        filterToggle.classList.toggle('is-active', open);
        filterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      if (!filtersColumn || !facets) return;

      if (isLg()) {
        setPageOverlay(false);
        document.body.classList.remove('overflow-hidden');
        filtersColumn.classList.toggle('lg:js-hidden', !open);
        if (grid) {
          grid.className = open
            ? grid.dataset.filtersOpenClasses
            : grid.dataset.filtersClosedClasses;
        }
      } else if (open) {
        facets.classList.add('is-open');
        facets.setAttribute('open', '');
        facets.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
        setPageOverlay(true);
      } else {
        facets.classList.remove('is-open');
        facets.removeAttribute('open');
        facets.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden');
        setPageOverlay(false);
      }
    }

    function apply({ skipUrl } = {}) {
      const q = state.q.trim().toLowerCase();
      const markets = selectedValues(root, 'market');
      const regions = selectedValues(root, 'region');
      const types = selectedValues(root, 'type');
      let visible = 0;

      items.forEach((item) => {
        const matchMarket = itemMatchesMarkets(item, markets);
        const matchRegion = !regions.length || regions.includes(item.dataset.region || '');
        const matchType = !types.length || types.includes(item.dataset.type || '');
        const matchSearch = !q || (item.dataset.title || '').includes(q);
        const show = matchMarket && matchRegion && matchType && matchSearch;
        item.classList.toggle('is-hidden', !show);
        if (show) visible += 1;
      });

      if (grid) {
        const visibleItems = items.filter((item) => !item.classList.contains('is-hidden'));
        visibleItems.sort((a, b) => {
          const at = a.dataset.title || '';
          const bt = b.dataset.title || '';
          return state.sort === 'title-descending' ? bt.localeCompare(at) : at.localeCompare(bt);
        });
        visibleItems.forEach((item) => grid.appendChild(item));
      }

      syncMarketImages(items, markets);

      if (empty) empty.hidden = visible > 0;

      if (!skipUrl && !state.syncingUrl) {
        writeUrlState({ markets, regions, types, q: state.q });
      }
    }

    function hydrateFromUrl() {
      const urlState = readUrlState();
      state.syncingUrl = true;
      applyCheckboxGroup(root, 'market', urlState.markets);
      applyCheckboxGroup(root, 'region', urlState.regions);
      applyCheckboxGroup(root, 'type', urlState.types);
      state.q = urlState.q;
      if (search) search.value = urlState.q;
      state.syncingUrl = false;
      apply({ skipUrl: true });
    }

    if (search) {
      search.addEventListener('input', () => {
        state.q = search.value || '';
        apply();
      });
    }

    root.querySelectorAll('[data-filter-checkbox]').forEach((input) => {
      input.addEventListener('change', () => apply());
    });

    if (filterToggle) {
      filterToggle.addEventListener('click', () => {
        if (isLg()) {
          setFiltersOpen(!state.filtersOpen);
        } else {
          setFiltersOpen(true);
        }
      });
    }

    root.querySelectorAll('[data-producer-filters-close]').forEach((btn) => {
      btn.addEventListener('click', () => setFiltersOpen(false));
    });

    if (pageOverlay) {
      pageOverlay.addEventListener('click', () => {
        if (!isLg() && state.filtersOpen) setFiltersOpen(false);
      });
    }

    const sortRoot = root.querySelector('.products-toolbar__sort');
    if (sortRoot) {
      const onSort = (value) => {
        if (!value) return;
        state.sort = value;
        apply();
      };
      sortRoot.addEventListener('change', (evt) => {
        onSort(evt.target?.value || evt.detail?.selectedValue);
      });
      sortRoot.addEventListener('click', (evt) => {
        const opt = evt.target.closest('[data-value]');
        if (opt) onSort(opt.getAttribute('data-value'));
      });
    }

    window.addEventListener('resize', () => {
      syncFacetsChrome();
      if (isLg()) setFiltersOpen(state.filtersOpen);
    });

    window.addEventListener('popstate', hydrateFromUrl);

    syncFacetsChrome();
    // Desktop: sidebar open. Mobile: closed — user opens via Filter button.
    setFiltersOpen(isLg());
    hydrateFromUrl();
  }

  function boot() {
    document.querySelectorAll('[data-producers-index]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (evt) => {
    const root = evt.target.querySelector?.('[data-producers-index]') || evt.target;
    if (root?.matches?.('[data-producers-index]')) {
      delete root.dataset.producersIndexReady;
      init(root);
    }
  });
})();
